import { useState, useMemo } from 'react';
import { useReadContract } from 'wagmi';
import { useChainId } from 'wagmi';
import { getContracts } from '@/config/contracts';
import {
  CrashGuardCoreABI,
  EmergencyExecutorABI,
  PythPriceMonitorABI,
  DEXAggregatorABI,
  PortfolioRebalancerABI
} from '@/config/abis';
import toast from 'react-hot-toast';

export const useCrashGuardCore = () => {
  const chainId = useChainId();
  const contracts = getContracts(chainId);

  return useMemo(() => ({
    address: contracts.CrashGuardCore as `0x${string}`,
    abi: CrashGuardCoreABI,
  }), [contracts.CrashGuardCore]);
};

export const useEmergencyExecutor = () => {
  const chainId = useChainId();
  const contracts = getContracts(chainId);

  return useMemo(() => ({
    address: contracts.EmergencyExecutor as `0x${string}`,
    abi: EmergencyExecutorABI,
  }), [contracts.EmergencyExecutor]);
};

export const usePythPriceMonitor = () => {
  const chainId = useChainId();
  const contracts = getContracts(chainId);

  return useMemo(() => ({
    address: contracts.PythPriceMonitor as `0x${string}`,
    abi: PythPriceMonitorABI,
  }), [contracts.PythPriceMonitor]);
};

export const useDEXAggregator = () => {
  const chainId = useChainId();
  const contracts = getContracts(chainId);

  return useMemo(() => ({
    address: contracts.DEXAggregator as `0x${string}`,
    abi: DEXAggregatorABI,
  }), [contracts.DEXAggregator]);
};

export const usePortfolioRebalancer = () => {
  const chainId = useChainId();
  const contracts = getContracts(chainId);

  return useMemo(() => ({
    address: contracts.PortfolioRebalancer as `0x${string}`,
    abi: PortfolioRebalancerABI,
  }), [contracts.PortfolioRebalancer]);
};

export const useDeposit = () => {
  const chainId = useChainId();
  const [isLoading, setIsLoading] = useState(false);
  const [txHash, setTxHash] = useState<string | null>(null);

  const depositToken = async (tokenAddress: string, amount: string, decimals: number) => {
    if (!chainId) return;

    setIsLoading(true);
    toast.loading('Processing deposit...', { id: 'deposit' });

    try {
      const { contractService } = await import('@/services/contractService');
      const hash = await contractService.depositAsset(tokenAddress, amount, decimals, chainId);

      setTxHash(hash);
      toast.dismiss('deposit');
      toast.success('Deposit successful! 🎉');
    } catch (error: any) {
      toast.dismiss('deposit');
      toast.error(`Deposit failed: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  return { depositToken, isLoading, txHash };
};

export const useWithdraw = () => {
  const chainId = useChainId();
  const [isLoading, setIsLoading] = useState(false);
  const [txHash, setTxHash] = useState<string | null>(null);

  const withdrawToken = async (tokenAddress: string, amount: string, decimals: number) => {
    if (!chainId) return;

    setIsLoading(true);
    toast.loading('Processing withdrawal...', { id: 'withdraw' });

    try {
      const { contractService } = await import('@/services/contractService');
      const hash = await contractService.withdrawAsset(tokenAddress, amount, decimals, chainId);

      setTxHash(hash);
      toast.dismiss('withdraw');
      toast.success('Withdrawal successful! 💰');
    } catch (error: any) {
      toast.dismiss('withdraw');
      toast.error(`Withdrawal failed: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  return { withdrawToken, isLoading, txHash };
};

export const usePortfolioData = (userAddress?: string) => {
  const chainId = useChainId();
  const contracts = getContracts(chainId);

  const { data: portfolio, isLoading, refetch } = useReadContract({
    address: contracts.CrashGuardCore as `0x${string}`,
    abi: CrashGuardCoreABI,
    functionName: 'getUserPortfolio',
    args: userAddress ? [userAddress as `0x${string}`] : undefined,
    query: {
      enabled: !!userAddress,
    },
  });

  return { portfolio, isLoading, refetch };
};

export const usePolicyData = (userAddress?: string) => {
  const chainId = useChainId();
  const contracts = getContracts(chainId);

  const { data: policy, isLoading, refetch } = useReadContract({
    address: contracts.CrashGuardCore as `0x${string}`,
    abi: CrashGuardCoreABI,
    functionName: 'getProtectionPolicy',
    args: userAddress ? [userAddress as `0x${string}`] : undefined,
    query: {
      enabled: !!userAddress,
    },
  });

  return { policy, isLoading, refetch };
};